<script lang="ts">
import { inject } from 'vue';

import { PhxsgIcon as PhxsgCoreIcon } from '@sg-core/src/View/Components/Atoms/Icon';

import PhxsgAutocompleteButton from './phxsg-autocomplete-button.vue';

import type { PhxsgAutocompleteButtonAttrs, PhxsgAutocompleteButtonEvents } from './phxsg-autocomplete-button.vue';
import type { HTMLAttributes } from 'vue';

export interface PhxsgAutocompleteFieldProps {
  formField: {
    class?: HTMLAttributes['class'];
  };
  labelAttrs: {
    text?: string;
    class?: HTMLAttributes['class'];
  };
  hasSelection: boolean;
  inputAttrs: {
    class?: HTMLAttributes['class'];
    id: string;
    type: string;
    autocomplete: string;
    name: string;
    value: string;
    role: string;
    placeholder: string;
    'aria-describedby'?: string;
    'aria-autocomplete': 'none' | 'inline' | 'list' | 'both';
    'aria-expanded': boolean;
    'aria-controls'?: string;
    'aria-activedescendant'?: string;
  };
  helpAttrs: {
    id: string;
    class?: HTMLAttributes['class'];
  };
  inputEvents: {
    onClick: () => void;
    onFocus: () => void;
    onBlur: () => void;
    onInput: (e: Event) => void;
    onKeydown: (e: KeyboardEvent) => void;
  };
  startIconAttrs: {
    id?: string;
    size: string;
  };
  buttonAttrs: PhxsgAutocompleteButtonAttrs;
  buttonEvents: PhxsgAutocompleteButtonEvents;
}

interface Props {
  clearButtonIcon?: string | false;
  clearButtonLabel: string;
  error?: string;
  fieldProps: PhxsgAutocompleteFieldProps;
  hasSelection: boolean;
  hasValue: boolean;
  openButtonLabel: string;
  openButtonIcon?: string | false;
  displayBothButtons?: boolean;
  alwaysDisplayClearButton?: boolean;
}
</script>

<script setup lang="ts">
const { error = '', displayBothButtons = false, alwaysDisplayClearButton = false } = defineProps<Props>();

const PhxsgIcon = inject('PhxsgIcon', PhxsgCoreIcon);
</script>

<template>
  <div v-bind="fieldProps.formField">
    <label v-if="fieldProps.labelAttrs.text" :for="fieldProps.inputAttrs.id" :class="fieldProps.labelAttrs.class">
      {{ fieldProps.labelAttrs.text }}
    </label>

    <div class="phxsg-autocomplete__field-wrapper">
      <div v-if="fieldProps.startIconAttrs.id" class="phxsg-autocomplete__input-icon">
        <PhxsgIcon :id="fieldProps.startIconAttrs.id" :size="fieldProps.startIconAttrs.size" />
      </div>

      <PhxsgAutocompleteButton
        v-show="(!hasSelection && !hasValue) || displayBothButtons"
        :button-attrs="fieldProps.buttonAttrs"
        :button-events="fieldProps.buttonEvents"
        :label="openButtonLabel"
        :icon="openButtonIcon"
        type="open"
      />

      <input v-bind="{ ...fieldProps.inputAttrs, ...fieldProps.inputEvents }" class="phxsg-autocomplete__input" />

      <PhxsgAutocompleteButton
        v-if="hasSelection || hasValue || alwaysDisplayClearButton"
        :button-attrs="fieldProps.buttonAttrs"
        :button-events="fieldProps.buttonEvents"
        :label="clearButtonLabel"
        :icon="clearButtonIcon"
        type="clear"
      />
    </div>

    <div v-if="error" v-bind="fieldProps.helpAttrs">{{ error }}</div>
  </div>
</template>
